import type { ExpDisplayUpdate, RealtimeExpRateMetric } from '@/domain/types'
import { EXP_TRANSITION_REVIEW_MS } from './exp-transition'

export const REALTIME_EXP_WINDOW_MS = 60_000
const HOUR_MS = 3_600_000
type Direction = RealtimeExpRateMetric['direction']
interface Point {
  sourceAt: number
  activeAt: number
  gain: number | null
  provisionalGain: number | null
  absolute: number
  approximate: boolean
}

/** Unbucketed source-time endpoints. Timer ticks never manufacture observations. */
export class SessionExpRateTracker {
  private points: Point[] = []
  private minimumSourceAt = -Infinity
  private resolution: string | null = null
  private direction: Direction = null
  private freshnessMs = 15_000

  setSamplingInterval(intervalMs: number): void {
    if (Number.isFinite(intervalMs) && intervalMs > 0) this.freshnessMs = Math.max(15_000, intervalMs * 3)
  }

  reset(sourceAt = -Infinity): void {
    this.points = []
    this.minimumSourceAt = sourceAt
    this.resolution = null
    this.direction = null
  }

  ingest(display: ExpDisplayUpdate, activeAt: number, invalidated = false): void {
    if (display.sourceAt === null || !display.point || !Number.isFinite(activeAt)) return
    const sourceAt = display.sourceAt
    if (sourceAt < this.minimumSourceAt || sourceAt < (this.points.at(-1)?.sourceAt ?? -Infinity)) return
    const transition = display.journeyTransition
    const key = transition ? `${transition.id ?? transition.fromSourceAt}:${transition.revision}:${transition.status}:${transition.toSourceAt}` : null
    const newResolution = key !== null && key !== this.resolution
    const unsafe = invalidated || newResolution && (transition?.status === 'discarded'
      || transition?.status === 'unavailable' || transition?.status === 'confirmed' && transition.kind === 'correction')
    const previous = this.points.at(-1)
    if (unsafe || previous && (sourceAt - previous.sourceAt > this.freshnessMs
      || activeAt < previous.activeAt || display.continuity === 'discontinuous' && !transition)) {
      this.reset(sourceAt)
    }
    if (key) this.resolution = key
    const point: Point = { sourceAt, activeAt, gain: display.netGain
      ?? (transition?.status === 'pending' && sourceAt < transition.fromSourceAt ? display.pendingNetGain ?? null : null),
      provisionalGain: display.pendingNetGain ?? display.netGain,
      absolute: display.point.absolute, approximate: display.approximate }
    const last = this.points.at(-1)
    if (last?.sourceAt === sourceAt) this.points[this.points.length - 1] = point
    else this.points.push(point)

    if (transition?.status === 'pending') {
      this.direction = null
      for (const entry of this.points) if (entry.sourceAt >= transition.fromSourceAt) entry.gain = null
    } else if (transition?.status === 'confirmed' && newResolution && !unsafe) {
      if (transition.netOffset == null || !Number.isFinite(transition.netOffset)) {
        this.reset(sourceAt)
        this.points.push(point)
      } else {
        for (const entry of this.points) {
          if (entry.sourceAt >= transition.fromSourceAt && entry.sourceAt <= transition.toSourceAt) {
            const corrected = entry.sourceAt >= (transition.correctionFromSourceAt ?? transition.fromSourceAt)
            entry.gain = corrected ? entry.absolute + transition.netOffset : entry.provisionalGain
            if (corrected) entry.approximate = display.approximate
          }
        }
      }
    }
    const cutoff = sourceAt - REALTIME_EXP_WINDOW_MS - EXP_TRANSITION_REVIEW_MS - this.freshnessMs
    let remove = 0
    while (remove + 1 < this.points.length && this.points[remove + 1]!.sourceAt < cutoff) remove++
    if (remove) this.points.splice(0, remove)
  }

  snapshot(now: number, average: number | null, displayStatus: ExpDisplayUpdate['status'], running: boolean): RealtimeExpRateMetric {
    const last = this.points.at(-1)
    const empty = (status: RealtimeExpRateMetric['status']): RealtimeExpRateMetric => {
      this.direction = null
      return { value: null, delta: null, status, direction: null, windowMs: REALTIME_EXP_WINDOW_MS,
        windowStartAt: null, updatedAt: last?.sourceAt ?? null, approximate: false }
    }
    if (!running) return empty('unavailable')
    if (displayStatus === 'pending-transition' || displayStatus === 'unresolved-level') return empty('pending')
    if (average === null || !Number.isFinite(average)) return empty('unavailable')
    if (!last) return empty('warming')
    if (now - last.sourceAt > this.freshnessMs) return empty('stale')
    const start = last.activeAt - REALTIME_EXP_WINDOW_MS
    const index = this.points.findIndex((point, i) => point.activeAt >= start && (i > 0 || point.activeAt === start))
    if (index < 0 || start < this.points[0]!.activeAt || last.gain === null) return empty('warming')
    const right = this.points[index]!
    const left = right.activeAt === start ? right : this.points[index - 1]!
    if (left.gain === null || right.gain === null || this.points.slice(index).some(point => point.gain === null)) return empty('pending')
    const fraction = right.activeAt === left.activeAt ? 0 : (start - left.activeAt) / (right.activeAt - left.activeAt)
    const startGain = left.gain + (right.gain - left.gain) * fraction
    const value = (last.gain - startGain) * HOUR_MS / REALTIME_EXP_WINDOW_MS
    const delta = value - average
    if (!Number.isFinite(value) || !Number.isFinite(delta) || last.activeAt <= 0) return empty('unavailable')
    const bias = Math.abs(last.gain / last.activeAt * HOUR_MS - average)
    const next: Direction = delta > 0 ? 'up' : delta < 0 ? 'down' : null
    if (next !== this.direction) this.direction = null
    const threshold = Math.max(1, Math.abs(average) * (this.direction ? .02 : .04)) + bias
    if (this.direction ? Math.abs(delta) <= threshold : Math.abs(delta) < threshold) this.direction = null
    else this.direction = next
    return { value, delta, status: 'ready', direction: this.direction, windowMs: REALTIME_EXP_WINDOW_MS,
      windowStartAt: last.sourceAt - REALTIME_EXP_WINDOW_MS, updatedAt: last.sourceAt,
      approximate: fraction !== 0 || this.points.slice(Math.max(0, index - 1)).some(point => point.approximate) }
  }
}
